/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';
import { FlexLayout, FlexItem } from '@waca/ca-ui-toolkit';

import SelectValueFilterPane from './SelectValueFilterPane';
import FilterPreferences from './FilterPreferences';
import ConditionBuilder from './ConditionBuilder';
import FilterLoadingSpinner from './FilterLoadingSpinner';

/*
This is a common filter component that allows a filter to be applied using a
slider control. There are also options to enter to/from filter values directly
via text boxes, invert the current selection and specify whether to include
blank values. The filter type can be to switched to a 'list' filter which allows the user
to select/deselectSVGIcon from a list of values.
*/
export default class NonNumericFilterPane extends Component<any, any> {
	// Define properties and types
	static propTypes = {
		inverted: PropTypes.bool,
		onChange: PropTypes.func.isRequired,
		totalValues: PropTypes.number.isRequired,
		selectedValuesCount: PropTypes.number,
		loading: PropTypes.bool,
		onClick: PropTypes.func.isRequired,
		onClear: PropTypes.func,
		onSearch: PropTypes.func.isRequired,
		allowSearch: PropTypes.bool,
		contentRenderer: PropTypes.func,
		getValue: PropTypes.func,
		showCondition: PropTypes.bool,
		allowCondition: PropTypes.bool,
		condition: PropTypes.object,
		readOnly: PropTypes.bool,
		showSearchInput: PropTypes.bool,
		conditionFlyoutContentContainerId: PropTypes.string,
		hasManualSelections: PropTypes.bool,
		hasMoreData: PropTypes.bool,
		readOnlyPrefs: PropTypes.bool,
		invertDisabled: PropTypes.bool,
		isSearching: PropTypes.bool,
	};

	// Define default property values
	static defaultProps = {
		inverted: false,
		decimalPlaces: 5,
		selectedValuesCount: 0,
		loading: false,
		allowSearch: true,
		showSearchInput: true,
		showCondition: undefined,
		allowCondition: true,
		condition: null,
		readOnly: false,
		hasManualSelections: false,
		hasMoreData: false,
		invertDisabled: false,
	};

	// States
	state = {
		isOpen: false,
	};

	// Event handlers
	_onChange = result => {
		this.props.onChange(result);
	};

	_buildResult = () => {
		const result = {
			inverted: this.props.inverted,
		};

		return result;
	};

	// Preferences have changed
	_onPreferencesChanged = preferences => {
		const result = { ...this._buildResult(), ...preferences, condition: this.props.condition };

		this.props.onChange(result);
	};

	// Event handlers
	_toggleSlideout = open => {
		this.setState({ isOpen: open });
	};

	_onConditionChange = v => {
		const result = { ...this._buildResult(), condition: v };

		this.props.onChange(result);
	};

	_renderCondition() {
		const { isOpen } = this.state;
		const {
			condition,
			inverted,
			readOnly,
			conditionFlyoutContentContainerId,
			hasManualSelections,
		} = this.props;

		return (
			<FlexItem margin={0}>
				<ConditionBuilder
					condition={condition}
					onChange={this._onConditionChange}
					open={isOpen}
					inverted={inverted}
					toggleSlideout={this._toggleSlideout}
					readOnly={readOnly}
					conditionFlyoutContentContainerId={conditionFlyoutContentContainerId}
					hasManualSelections={hasManualSelections}
				/>
			</FlexItem>
		);
	}

	_shouldShowCondition = () => {
		const { showCondition, allowCondition } = this.props;

		if (showCondition !== undefined && showCondition !== null) {
			return showCondition; // legacy, takes precedence
		}

		return allowCondition;
	};

	_renderNonNumericFilterPane = () => {
		const {
			inverted,
			selectedValuesCount,
			totalValues,
			loading,
			onClick,
			onSearch,
			getValue,
			contentRenderer,
			allowSearch,
			showSearchInput,
			readOnly,
			hasMoreData,
			invertDisabled,
			isSearching,
		} = this.props;

		const borderStyle = {
			borderColor: '#F2F2F2',
			borderWidth: '1px',
			borderStyle: 'solid',
		};

		return (
			<FlexLayout
				justifyContent="space-between"
				fullHeight
				direction="column"
				data-tid="nonNumericFilterPane"
			>
				<FlexItem>{this._shouldShowCondition() && this._renderCondition()}</FlexItem>
				<FlexItem grow={1} style={borderStyle}>
					<SelectValueFilterPane
						totalValues={totalValues}
						selectedValuesCount={selectedValuesCount}
						loading={loading}
						allowSearch={allowSearch}
						showSearchInput={showSearchInput}
						onClick={onClick}
						onChange={this._onChange}
						onSearch={onSearch}
						isSearching={isSearching}
						getValue={getValue}
						contentRenderer={contentRenderer}
						border="1px"
						border-color="gray"
						readOnly={readOnly}
						hasMoreData={hasMoreData}
					/>
				</FlexItem>
				<FlexItem gutter={[2, 0, 0, 0]}>
					<FilterPreferences
						inverted={inverted}
						includeNulls={false}
						onChange={this._onPreferencesChanged}
						onClear={this.props.onClear}
						showIncludeNull={false}
						selectedValuesCount={selectedValuesCount}
						returnToTree={() => {}}
						readOnly={readOnly || this.props.readOnlyPrefs}
						invertDisabled={invertDisabled}
					/>
				</FlexItem>
			</FlexLayout>
		);
	};

	render() {
		const { loading } = this.props;

		if (loading) {
			return <FilterLoadingSpinner />;
		}

		return this._renderNonNumericFilterPane();
	}
}
